import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import store from '../store/modules/index';
import { User } from '@/store/models/ProfileModel';
import AuthService from './auth-service';
import { AdminCustomer } from '@/store/models/CustomerModel';
import { handleResponse } from '@/helpers/ApiHelper';
import { ChangeOrderlineDueDateModel } from '@/store/models/AdminModel';

const adminUrl = `${process.env.VUE_APP_ROOT_API}/Administrator`;
const authUrl = `${process.env.VUE_APP_AUTH_API}`;
const webAccessUrl = `${process.env.VUE_APP_ROOT_API}/WebAccess`;
const commonUrl = `${process.env.VUE_APP_COMMON_API}/Administrator`;

export const AdminService = {
  ChangeOrderlineDueDate(model: ChangeOrderlineDueDateModel): Promise<AxiosResponse> {
    return axios.post(`${commonUrl}/ChangeOrderlineDueDate`, model);
  },
  getAllCustomers(shouldOnlyShowTransparency: boolean) {
    return axios.get(
      `${adminUrl}/GetCustomers?onlyTransparency=${shouldOnlyShowTransparency}`
    );
  },
  getCustomer(customerId: string): Promise<AdminCustomer> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get(
          `${adminUrl}/GetCustomer?customerId=${customerId}`
        );
        handleResponse(response);
        response.data.Customer.Users = response.data.Users;
        response.data.Customer.TotalInvoiced =
          response.data.TotalInvoiced.TotalAmount | 0;
        response.data.Customer.TotalInvoicedYear =
          response.data.TotalInvoicedYear.TotalAmount | 0;
        let returnValue = new AdminCustomer(response.data.Customer);
        return resolve(returnValue);
      } catch (error) {
        return reject(error);
      }
    });
  },
  getActivityByCustomer(customerId: string, queryDate: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get(
          `${adminUrl}/GetActivitiesForCustomer?customerId=${customerId}&activitiyDate=${queryDate}`
        );
        handleResponse(response);
        return resolve(response.data);
      } catch (error) {
        return reject(error);
      }
    });
  },
  getActivityByUser(userId: string, queryDate: string): Promise<any> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get(
          `${adminUrl}/GetActivitiesForUser?userId=${userId}&activitiyDate=${queryDate}`
        );
        handleResponse(response);
        return resolve(response.data);
      } catch (error) {
        return reject(error);
      }
    });
  },
  impersonateUser(model: any) {
    return axios.post(`${adminUrl}/impersonate`, model);
  },
  async saveCustomerData(model: AdminCustomer): Promise<AxiosResponse> {
    let responseModel: any = {
      PublicId: model.PublicId,
      Id: model.Id,
      Brand: model.Brand,
      Comments: model.Comments,
      ContactEmail: model.ContactEmail,
      OD: model.OD,
      Options: model.Options,
      Term: model.Term,
      ShipTo: model.ShipTo
    };
    return axios.post(`${adminUrl}/SaveCustomer`, responseModel);
  },
  async impersonateCustomer(model: FormData, store: any): Promise<AxiosResponse> {
    let username = model.get('userName') as string;
    let customerId = model.get('customerId') as string;
    const requestOptions: AxiosRequestConfig = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      withCredentials: true
    };
    return new Promise(async (resolve, reject) => {
      let response = await axios.post(
        `${authUrl}/impersonate/`,
        { username, customerId },
        requestOptions
      );
      try {
        const token = handleImpersonationResponse(response);
        // login successful if there's a jwt token in the response
        if (token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          localStorage.removeItem('user');
          const user = new User(token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${user.sessionId}`;
          let displayNameResponse = await AuthService.getDisplayName();
          if (displayNameResponse.data) user.displayName = displayNameResponse.data;
          localStorage.setItem('user', JSON.stringify(user));
          return resolve(token);
        }
      } catch (error) {
        return reject(error);
      }
    });
  },
  getImpersonatedCustomer() {
    return axios.get(`${adminUrl}/GetImpersonatedCustomer`);
  },
  addActivity(entry: string) {
    return axios.get(`${webAccessUrl}/AddActivity/${entry}`);
  },
  addActivityStream(entry: string) {
    return axios.get(`${webAccessUrl}/AddActivityStream/${entry}`);
  },
  getActivitiesByDate(lastActivityId: string) {
    return axios.get(`${webAccessUrl}/GetActivityStream/${lastActivityId}`);
  },
  getActivityStreamByCustomerName(customerName: string, shouldIncludeAdmin: boolean) {
    return axios.get(
      `${webAccessUrl}/GetActivityStreamByCustomerName?customerName=${customerName}&shouldIncludeAdmin=${shouldIncludeAdmin}`
    );
  },
  processOrderByFile(formData: FormData, config: any = null) {
    return axios.post(`${adminUrl}/ProcessOrderByFile`, formData, config);
  },
  refreshAllCaches() {
    return axios.get(`${adminUrl}/RefreshAllCache`);
  },
  refreshCustomerCache() {
    return axios.get(`${adminUrl}/RefreshCustomerCache`);
  },
  refreshDieCache() {
    return axios.get(`${adminUrl}/RefreshDieCache`);
  },
  getCodeManagerInfo(orderlineId: string) {
    return axios.get(`${adminUrl}/GetCodeManagerInfo?orderlineId=${orderlineId}`);
  },
  refreshMaterial() {
    return axios.get(`${adminUrl}/RefreshMaterialCache`);
  },
  async sendAmazonTspReport(endDate: string) {
    return axios.get(`${adminUrl}/GenerateAmazonReportEmail?endDate=${endDate}`);
  }
};

function handleImpersonationResponse(response: any) {
  const data = response.data;
  if (response.status !== 200) {
    if (response.status === 401) {
      // auto logout if 401 response returned from api
      store.dispatch('profile/logout');
      location.reload();
    }

    const error = (data && data.message) || response.statusText;
    return error;
  }

  return data;
}

export default AdminService;
